import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge } from 'src/components/atoms';
import { BadgeVariant } from 'src/components/atoms/Badge/Badge.styles';
import { ColumnType, OnRowClickType } from 'src/components/molecules';
import { createEditableReport } from 'src/creators';
import {
  AppliedFiltersType,
  ReportStatus,
  SearchByInputType,
  TagType,
  useCasesQuery,
  useMeQuery,
  UserType,
} from 'src/graphql';
import { useIsFeatureEnabled, useTableColumns } from 'src/hooks';
import { selectCurrentUser } from 'src/selectors/selectCurrentUser';
import { AppDispatch } from 'src/store';
import { FeatureFlag, getCaseReadableId } from 'src/types';
import {
  capitaliseFirstLetter,
  graphQLReportSourceToLocalReportSource,
} from 'src/utils';
import * as Types from '../../../types';
import { hiddenDots } from '../Case/helpers/hiddenDots';
import {
  CaseLeadCasesTableCell,
  CaseLinksTableCell,
  CaseTagsWithOverflow,
} from './components';
import { getFormattedDate } from './helpers/formatDate';
import { caseSearchOptions } from './helpers/searchTableHelper';

const columns = (caseLinksVisible?: boolean): ColumnType[] => [
  { label: 'Status', value: 'status' },
  { label: 'Case Name', value: 'name' },
  { label: 'Case Id', value: 'id' },
  { label: 'Case Tags', value: 'tags' },
  { label: 'Category', value: 'category' },
  { label: 'Added Date', value: 'createdAt' },
  { label: 'Created Date', value: 'companyCreatedAt' },
  { label: 'Last Updated', value: 'updatedAt' },
  { label: 'Assigned Case Lead', value: 'caseLead', showOverflow: true },
  { label: 'Case Team Members', value: '' },
  ...(caseLinksVisible
    ? [{ label: 'Linked Cases', value: 'caseLinksCount' }]
    : []),
  { label: 'Source', value: 'Reports.Source' },
];

const columnInfoTooltips = [
  {
    columnLabel: 'Added Date',
    tooltip:
      'This indicates when the case was incorporated into Vault, even if the case itself was created in the past.',
  },
  {
    columnLabel: 'Created Date',
    tooltip: `This signifies the original creation date of the case in the 'real world', providing context about when the case was initially established.`,
  },
];

const sortableColumns = [
  'Case Name',
  'Case Id',
  'Added Date',
  'Created Date',
  'Last Updated',
  'Status',
  'Assigned Case Lead',
];

const defaultHiddenColumns = ['Case Team Members'];

const getCategoryNames = (caseData: {
  Categories?: Array<{ name: string }>;
  Reports?: Array<{
    DeterminedCategory?: { name: string } | null;
    ReportedCategory?: { name: string } | null;
  }> | null;
}) => {
  if (caseData.Categories?.length) {
    return caseData.Categories.map((cat) => cat.name).join(', ');
  }
  const firstReport = caseData.Reports?.[0];
  return (
    firstReport?.DeterminedCategory?.name ??
    firstReport?.ReportedCategory?.name ??
    ''
  );
};

export const useCaseTable = (
  rows: number,
  page: number,
  redirect: (url: string) => void,
  appliedFilters: AppliedFiltersType,
  caseQueryFields: SearchByInputType[] | null = null,
  caseLinkingEnabled?: boolean,
) => {
  const dispatch = useDispatch<AppDispatch>();
  const assignee = useSelector(selectCurrentUser);

  const isLegacyMode = useSelector(
    (state: Types.State) => state.organisation?.isLegacyMode,
  );

  const { data: viewerData } = useMeQuery();
  const viewer: UserType = viewerData?.me! as UserType;

  const {
    visibleColumns,
    sortedColumn,
    sortColumns,
    variables,
    search: searchCases,
    updateVisibleColumns,
    searchValue,
  } = useTableColumns(
    rows,
    page,
    columns(caseLinkingEnabled),
    defaultHiddenColumns,
    'companyCreatedAt',
    'desc',
    caseSearchOptions,
    appliedFilters,
  );

  const queryVariables = useMemo(() => {
    if (caseQueryFields && caseQueryFields?.length > 0) {
      return { ...variables, searchBy: [...caseQueryFields] };
    }
    return variables;
  }, [caseQueryFields, variables]);

  const {
    data,
    isLoading,
    isError,
    refetch: refetchCasesPaginated,
  } = useCasesQuery(queryVariables);

  const tableData = (data?.casesPaginated.items ?? []).map((c) => {
    const canViewCaseDetails = Boolean(
      c.AssociatedPermissions?.canViewCaseDetails,
    );
    const canAdministerCase = Boolean(
      c.AssociatedPermissions?.canAdministerCasePermissions,
    );
    const canAdministerReport = Boolean(
      c.Reports?.[0]?.AssociatedPermissions?.canAdministerReportPermissions,
    );

    const caseStatus = capitaliseFirstLetter(c.status);

    const caseHasTags = Boolean(c.CaseTags?.length);
    const createdAt =
      c.status === ReportStatus.Draft
        ? null
        : (c.CaseAttributes?.companyCreatedAt ?? c.createdAt);
    const isDraft = c.status === ReportStatus.Draft;
    const reporterIds = c.Reports?.map((report) => report.Reporter?.id).filter(
      (id): id is number => id !== undefined && id !== null,
    );

    return {
      cellData: [
        canViewCaseDetails ? (
          <Badge
            key={c.id}
            variant={c.status?.toLowerCase() as BadgeVariant}
            text={caseStatus}
            data-reportid={c.Reports?.[0].id}
          />
        ) : (
          hiddenDots
        ),
        c.name?.length ? c.name : 'Untitled Case',
        getCaseReadableId(c.id),
        caseHasTags && canViewCaseDetails ? (
          <CaseTagsWithOverflow
            tags={c.CaseTags as TagType[]}
            size="small"
            tagsToDisplay={1}
            key={c.id}
            readOnly
            shouldUseTooltipPortal
          />
        ) : !canViewCaseDetails ? (
          hiddenDots
        ) : (
          ''
        ),
        canViewCaseDetails ? getCategoryNames(c) : hiddenDots,
        getFormattedDate(c.createdAt, canViewCaseDetails),
        getFormattedDate(createdAt, canViewCaseDetails),
        getFormattedDate(c.updatedAt, canViewCaseDetails),
        canViewCaseDetails ? (
          <CaseLeadCasesTableCell
            key={c.id}
            caseId={c.id}
            availableCaseLeads={c?.AllCaseLeads}
            caseLead={c?.CaseLead}
            canAdministerCase={canAdministerCase}
            canAdministerReport={canAdministerReport}
            refetch={refetchCasesPaginated}
            isDraft={isDraft}
            reporterIds={reporterIds}
          />
        ) : (
          hiddenDots
        ),
        canViewCaseDetails
          ? c.CaseTeam?.filter((m) => m.id !== c.CaseLead?.id)
              .map((member) => member.name)
              .join(', ')
          : hiddenDots,
        ...(caseLinkingEnabled
          ? [
              <CaseLinksTableCell
                key={`${c.id}-links`}
                linksCount={c.caseLinksCount}
                canViewCaseDetails={canViewCaseDetails}
              />,
            ]
          : []),
        c.Reports?.map(
          (report, index) =>
            `${graphQLReportSourceToLocalReportSource(
              report.source,
              canViewCaseDetails,
            )} ${c?.Reports && index === c.Reports?.length - 1 ? '' : ', '}`,
        ),
      ],
      row: c.id,
      data: {
        status: c.status,
        reportId: c.Reports?.[0]?.id,
        existingReport: c.Reports?.[0]?.existingReport,
      },
      tableRowData: {
        caseName: c.name?.length ? c.name : 'Untitled Case',
        caseReadableId: getCaseReadableId(c.id),
        caseId: c.id,
        caseTags:
          caseHasTags && canViewCaseDetails ? (
            <CaseTagsWithOverflow
              tags={c.CaseTags as TagType[]}
              size="small"
              tagsToDisplay={1}
              key={c.id}
              readOnly
              shouldUseTooltipPortal
            />
          ) : !canViewCaseDetails ? (
            hiddenDots
          ) : (
            ''
          ),
        category: canViewCaseDetails ? getCategoryNames(c) : hiddenDots,
        addedDate: getFormattedDate(c.createdAt, canViewCaseDetails),
        createdDate: getFormattedDate(createdAt, canViewCaseDetails),
        lastUpdate: getFormattedDate(c.updatedAt, canViewCaseDetails),
        status: canViewCaseDetails ? c.status?.toLowerCase() : hiddenDots,
        caseLead: canViewCaseDetails ? c?.CaseLead?.name : hiddenDots,
        caseTeam: canViewCaseDetails
          ? c.CaseTeam?.filter((m) => m.id !== c.CaseLead?.id).map(
              (member) => member.name,
            )
          : hiddenDots,
        assignCaseLead: canViewCaseDetails ? (
          <CaseLeadCasesTableCell
            key={c.id}
            caseId={c.id}
            availableCaseLeads={c?.AllCaseLeads}
            caseLead={c?.CaseLead}
            canAdministerCase={canAdministerCase}
            canAdministerReport={canAdministerReport}
            refetch={refetchCasesPaginated}
            isDraft={isDraft}
            reporterIds={reporterIds}
          />
        ) : null,
        caseLinks: canViewCaseDetails ? c.caseLinksCount : hiddenDots,
        source: c.Reports?.map(
          (report, index) =>
            `${graphQLReportSourceToLocalReportSource(
              report.source,
              canViewCaseDetails,
            )} ${c?.Reports && index === c.Reports?.length - 1 ? '' : ', '}`,
        ),
        data: {
          status: c.status,
          reportId: c.Reports?.[0]?.id ?? '',
          existingReport: c.Reports?.[0]?.existingReport ?? false,
        },
      },
    };
  });

  const openReport: OnRowClickType<{
    status: ReportStatus;
    reportId: string;
    existingReport: boolean;
  }> = (caseId, openReportData, e) => {
    const clickTarget = e.target as HTMLElement;
    if (
      clickTarget
        .getAttribute('data-testid')
        ?.includes('cell-Assigned Case Lead')
    ) {
      return;
    }

    if (openReportData?.reportId) {
      const selectedCase = (data?.casesPaginated.items ?? []).find(
        (c) => c.id === caseId,
      );

      if (!selectedCase?.AssociatedPermissions?.canViewCase) return;
    }

    if (openReportData?.status === ReportStatus.Draft) {
      const path = openReportData.existingReport
        ? 'create-existing-report'
        : 'create-report';
      return redirect(`${path}/${openReportData.reportId}`);
    }
    return redirect(`case?id=${caseId}`);
  };

  const isReportIntakeImprovementsEnabled = useIsFeatureEnabled(
    FeatureFlag.REPORT_INTAKE_IMPROVEMENTS,
  );
  const isUseNormalizedReportDataEnabled = useIsFeatureEnabled(
    FeatureFlag.USE_NORMALIZED_REPORT_DATA,
  );
  const isReportIntakeImprovementsDraftEnabled = useIsFeatureEnabled(
    FeatureFlag.REPORT_INTAKE_IMPROVEMENTS_DRAFT,
  );

  const draftReportFeatureFlagsEnabled =
    isReportIntakeImprovementsDraftEnabled &&
    isUseNormalizedReportDataEnabled &&
    isReportIntakeImprovementsEnabled;

  const addNewReport = useCallback(() => {
    assignee &&
      dispatch(
        createEditableReport(
          (reportId: string) => redirect(`/create-report/${reportId}`),
          false,
          draftReportFeatureFlagsEnabled,
          isLegacyMode,
        ),
      );
  }, [
    assignee,
    dispatch,
    redirect,
    draftReportFeatureFlagsEnabled,
    isLegacyMode,
  ]);

  const addExistingReport = useCallback(() => {
    assignee &&
      dispatch(
        createEditableReport(
          (reportId: string) => redirect(`/create-existing-report/${reportId}`),
          true,
          draftReportFeatureFlagsEnabled,
          isLegacyMode,
        ),
      );
  }, [
    assignee,
    dispatch,
    redirect,
    draftReportFeatureFlagsEnabled,
    isLegacyMode,
  ]);

  return {
    columns,
    sortableColumns,
    visibleColumns,
    columnInfoTooltips,
    updateVisibleColumns,
    tableData,
    isLoading,
    isError,
    caseSearchOptions,
    searchCases,
    addNewReport,
    addExistingReport,
    sortColumns,
    openReport,
    sortedColumn,
    count: data?.casesPaginated?.count,
    searchValue,
    viewer,
  };
};
